import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { BASE_URL } from "./constants";
import axios from 'axios';

const BACKEND_API_BASE = `${BASE_URL}/youtube/api/youtube`;
// const BACKEND_API_BASE = "http://localhost:8000/youtube/api/youtube";

// Error parsing function
function parseErrorMessage(error) {
  if (!error) return "An error occurred";
  
  // Handle tuple-style errors like "('invalid_grant: Bad Request', {'error': 'invalid_grant', 'error_description': 'Bad Request'})"
  if (typeof error === 'string' && error.includes("('")) {
    try {
      const match = error.match(/\('([^']+)',/);
      if (match && match[1]) {
        return match[1];
      }
    } catch (e) {
      // If parsing fails, continue with other methods
    }
  }
  if (typeof error === 'string') {
    try {
      const parsed = JSON.parse(error);
      return parsed.error || parsed.message || parsed.detail || error;
    } catch {
      return error;
    }
  }
  return error?.error || error?.message || error?.detail || "Request failed";
}

// Generic axios function for API calls
async function fetchBackend(url, options = {}) {
  const token = localStorage.getItem("authToken");
  
  try {
    const response = await axios({
      url,
      method: options.method || 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
        ...options.headers,
      },
      data: options.body,
      ...options,
    });

    return response.data;
  } catch (error) {
    const rawMsg = error.response?.data?.error || error.response?.data?.message || error.response?.data?.detail || error.message || "Request failed";
    const parsedMsg = parseErrorMessage(rawMsg);
    const e = new Error(parsedMsg);
    e.status = error.response?.status;
    e.details = error.response?.data;
    throw e;
  }
}

// Generic axios function for file uploads
async function fetchBackendFile(url, formData) {
  const token = localStorage.getItem("authToken");
  
  try {
    const response = await axios.post(url, formData, {
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
        'Content-Type': 'multipart/form-data',
      },
    });

    return response.data;
  } catch (error) {
    const rawMsg = error.response?.data?.error || error.response?.data?.message || error.response?.data?.detail || error.message || "Request failed";
    const parsedMsg = parseErrorMessage(rawMsg);
    const e = new Error(parsedMsg);
    e.status = error.response?.status;
    e.details = error.response?.data;
    throw e;
  }
}

// Query Keys
export const queryKeys = {
  videos: ['videos'],
  categories: ['categories'],
  authStatus: ['authStatus'],
  videoByUuid: (uuid) => ['videoByUuid', uuid],
  videosByCategory: (categoryId) => ['videosByCategory', categoryId],
};

// ==================== QUERIES ====================

// Get all videos
export function useVideos() {
  return useQuery({
    queryKey: queryKeys.videos,
    queryFn: () => fetchBackend(`${BACKEND_API_BASE}/videos/`),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
}

// Get all categories
export function useCategories() {
  return useQuery({
    queryKey: queryKeys.categories,
    queryFn: () => fetchBackend(`${BACKEND_API_BASE}/categories/`),
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
  });
}

// Get auth status
export function useAuthStatus(canManage = false) {
  return useQuery({
    queryKey: queryKeys.authStatus,
    queryFn: () => fetchBackend(`${BACKEND_API_BASE}/auth/status/`),
    staleTime: 30 * 1000, // 30 seconds
    refetchInterval: canManage ? 60 * 1000 : false, // Only refetch every minute if user can manage
    enabled: canManage, // Only fetch if user can manage
  });
}

// Get video by UUID (public access)
export function useVideoByUuid(uuid) {
  return useQuery({
    queryKey: queryKeys.videoByUuid(uuid),
    queryFn: async () => {
      const url = `${BACKEND_API_BASE}/videos/public/${uuid}/`;
      const res = await fetch(url);
      const json = res.ok ? await res.json().catch(() => ({})) : null;
      if (!res.ok) {
        const rawMsg = json?.error || "Video not found";
        const parsedMsg = parseErrorMessage(rawMsg);
        const e = new Error(parsedMsg);
        e.status = res.status;
        throw e;
      }
      return json;
    },
    enabled: !!uuid,
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
  });
}

// Get videos by category
export function useVideosByCategory(categoryId) {
  return useQuery({
    queryKey: queryKeys.videosByCategory(categoryId),
    queryFn: () => fetchBackend(`${BACKEND_API_BASE}/videos/by_category/?category_id=${categoryId}`),
    enabled: !!categoryId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}


// ==================== MUTATIONS ====================

// Sync data mutation
export function useSyncData() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: () => fetchBackend(`${BACKEND_API_BASE}/auth/sync/`, { method: "POST" }),
    onSuccess: (data) => {
      toast.success(data?.message || "Data synced successfully");
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: queryKeys.videos });
      queryClient.invalidateQueries({ queryKey: queryKeys.categories });
      queryClient.invalidateQueries({ queryKey: queryKeys.authStatus });
    },
    onError: (error) => {
      const errorMessage = error?.message || "Failed to sync data";
      toast.error(errorMessage);
      
      // If it's an auth error, invalidate auth status
      if (error?.status === 401 || error?.status === 403) {
        queryClient.invalidateQueries({ queryKey: queryKeys.authStatus });
      }
    },
  });
}

// Update video mutation
export function useUpdateVideo() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ videoId, title, description, category }) =>
      fetchBackend(`${BACKEND_API_BASE}/videos/${videoId}/`, {
        method: "PUT",
        body: { title, description, category },
      }),
    onSuccess: () => {
      toast.success("Video updated successfully. Please reload the page");
      queryClient.invalidateQueries({ queryKey: queryKeys.videos });
    },
    onError: (error) => {
      const errorMessage = error?.message || error?.details?.error || error?.details?.detail || "Failed to update video";
      toast.error(errorMessage);
    },
  });
}

// Delete video mutation
export function useDeleteVideo() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ videoId }) =>
      fetchBackend(`${BACKEND_API_BASE}/videos/${videoId}/`, {
        method: "DELETE",
      }),
    onSuccess: () => {
      toast.success("Video deleted successfully");
      queryClient.invalidateQueries({ queryKey: queryKeys.videos });
    },
    onError: (error) => {
      const errorMessage = error?.message || error?.details?.error || error?.details?.detail || "Failed to delete video";
      toast.error(errorMessage);
    },
  });
}

// Upload video mutation
export function useUploadVideo() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ file, metadata }) => {
      const formData = new FormData();
      formData.append("video_file", file);
      formData.append("title", metadata.snippet.title);
      formData.append("description", metadata.snippet.description || "");
      
      if (metadata.snippet.categoryId) {
        formData.append("category_id", metadata.snippet.categoryId);
      }

      return fetchBackendFile(`${BACKEND_API_BASE}/videos/upload_to_youtube/`, formData);
    },
    onSuccess: () => {
      toast.success("Video uploaded successfully");
      queryClient.invalidateQueries({ queryKey: queryKeys.videos });
    },
    onError: (error) => {
      const errorMessage = error?.message || error?.details?.error || error?.details?.detail || "Failed to upload video";
      toast.error(errorMessage);
    },
  });
}

// Create category mutation
export function useCreateCategory() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ name }) =>
      fetchBackend(`${BACKEND_API_BASE}/categories/`, {
        method: "POST",
        body: { name },
      }),
    onSuccess: () => {
      toast.success("Category created successfully");
      queryClient.invalidateQueries({ queryKey: queryKeys.categories });
    },
    onError: (error) => {
      const errorMessage = error?.response?.data?.name?.[0] || error?.message || error?.details?.error || error?.details?.detail || "Failed to create category.";
      toast.error(errorMessage);
    },
  });
}

// Update category mutation
export function useUpdateCategory() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, video_category, name, description }) =>
      fetchBackend(`${BACKEND_API_BASE}/categories/${id}/`, {
        method: "PUT",
        body: { video_category, name, description },
      }),
    onSuccess: () => {
      toast.success("Category updated successfully");
      queryClient.invalidateQueries({ queryKey: queryKeys.categories });
    },
    onError: (error) => {
      const errorMessage = error?.message || error?.details?.error || error?.details?.detail || "Failed to update category";
      toast.error(errorMessage);
    },
  });
}

// Delete category mutation
export function useDeleteCategory() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id }) =>
      fetchBackend(`${BACKEND_API_BASE}/categories/${id}/`, {
        method: "DELETE",
      }),
    onSuccess: () => {
      toast.success("Category deleted successfully");
      queryClient.invalidateQueries({ queryKey: queryKeys.categories });
    },
    onError: (error) => {
      const errorMessage = error?.message || error?.details?.error || error?.details?.detail || "Failed to delete category";
      toast.error(errorMessage);
    },
  });
}

// Sync video metadata mutation
export function useSyncVideoMetadata() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ videoId }) =>
      fetchBackend(`${BACKEND_API_BASE}/videos/${videoId}/update_youtube_metadata/`, {
        method: "POST",
      }),
    onSuccess: () => {
      toast.success("Video metadata synced successfully");
      queryClient.invalidateQueries({ queryKey: queryKeys.videos });
    },
    onError: (error) => {
      const errorMessage = error?.message || error?.details?.error || error?.details?.detail || "Failed to sync video metadata";
      toast.error(errorMessage);
    },
  });
}

// Get auth URL (simple query, not cached)
export function useGetAuthUrl() {
  return useQuery({
    queryKey: ['authUrl'],
    queryFn: () => fetchBackend(`${BACKEND_API_BASE}/auth/login/`),
    enabled: false, // Don't fetch automatically
    staleTime: 0, // Don't cache
  });
}

// ==================== COMBINED HOOKS ====================

// Hook for loading videos and categories together
export function useVideosAndCategories() {
  const videosQuery = useVideos();
  const categoriesQuery = useCategories();
  
  return {
    videos: videosQuery.data || [],
    categories: categoriesQuery.data || [],
    isLoading: videosQuery.isLoading || categoriesQuery.isLoading,
    isError: videosQuery.isError || categoriesQuery.isError,
    error: videosQuery.error || categoriesQuery.error,
    refetch: () => {
      videosQuery.refetch();
      categoriesQuery.refetch();
    },
  };
}

// Hook for admin dashboard data
export function useAdminDashboardData(canManage = false) {
  const videosQuery = useVideos();
  const categoriesQuery = useCategories();
  const authStatusQuery = useAuthStatus(canManage);
  
  return {
    videos: videosQuery.data || [],
    categories: categoriesQuery.data || [],
    authStatus: authStatusQuery.data,
    isLoading: videosQuery.isLoading || categoriesQuery.isLoading || (canManage && authStatusQuery.isLoading),
    isError: videosQuery.isError || categoriesQuery.isError || (canManage && authStatusQuery.isError),
    error: videosQuery.error || categoriesQuery.error || (canManage && authStatusQuery.error),
    refetch: () => {
      videosQuery.refetch();
      categoriesQuery.refetch();
      if (canManage) authStatusQuery.refetch();
    },
  };
}

// Hook for patient page data (no auth required)
export function usePatientPageData() {
  const videosQuery = useVideos();
  const categoriesQuery = useCategories();
  
  return {
    videos: videosQuery.data || [],
    categories: categoriesQuery.data || [],
    isLoading: videosQuery.isLoading || categoriesQuery.isLoading,
    isError: videosQuery.isError || categoriesQuery.isError,
    error: videosQuery.error || categoriesQuery.error,
    refetch: () => {
      videosQuery.refetch();
      categoriesQuery.refetch();
    },
  };
}
